/**
 * CommandCameraBoard — §1 "surveillance operations room" cameras renderer.
 *
 * Status-board rows: LED dot (pulsing green online / static red offline),
 * camera name (sans font-medium), mono IMEI muted, battery bar (h-1.5 w-14
 * with level-based fill), 4-bar signal indicator (ascending bars filled per
 * signal level), last-seen mono right-aligned, actions dropdown.
 *
 * Selection checkbox leads the row when selectionMode is active.
 * All handlers pass through from CamerasPage — no new queries, no hooks.
 *
 * data-testid="command-camera-row" on each row (for tests + QA).
 */

import {
  MoreVertical,
  Trash2,
  Settings,
  MapPin,
  Signal,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip } from '@/components/ui/tooltip'
import { formatRelativeTimeShort } from '@/lib/utils'
import type { Camera } from '@/types'

// ── sub-components ─────────────────────────────────────────────────────────────

function LedDot({ isOnline }: { isOnline: boolean | null }) {
  if (isOnline === true) {
    return (
      <span
        data-testid="led-online"
        className="inline-block h-2 w-2 rounded-full bg-[hsl(150,65%,45%)] animate-pulse flex-shrink-0"
        aria-label="Online"
      />
    )
  }
  if (isOnline === null) {
    // FTP camera — status unknown, neutral dim dot
    return (
      <span
        data-testid="led-unknown"
        className="inline-block h-2 w-2 rounded-full bg-muted-foreground/30 flex-shrink-0"
        title="Status unknown"
        aria-label="Status unknown"
      />
    )
  }
  // is_online === false — explicitly offline
  return (
    <span
      data-testid="led-offline"
      className="inline-block h-2 w-2 rounded-full bg-destructive flex-shrink-0"
      aria-label="Offline"
    />
  )
}

function BatteryBar({ level }: { level: number | null }) {
  if (level === null) {
    return (
      <div className="h-1.5 w-14 bg-muted rounded overflow-hidden flex-shrink-0 opacity-30" />
    )
  }
  const pct = Math.min(100, Math.max(0, level))
  const fillClass =
    level > 50
      ? 'bg-[hsl(150,65%,45%)]'
      : level > 20
        ? 'bg-primary'
        : 'bg-destructive'
  return (
    <Tooltip content={`Battery ${level}%`}>
      <div className="h-1.5 w-14 bg-muted rounded overflow-hidden flex-shrink-0">
        <div
          className={`h-full ${fillClass} transition-all duration-300`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Tooltip>
  )
}

/** 4 ascending bars — each 1.5px wide, heights step 3/5/7/9 px. */
function SignalBars4({ level }: { level: number | null }) {
  const bars = [3, 5, 7, 9]
  const active = level ?? 0
  return (
    <Tooltip content={level === null ? 'Signal unknown' : `Signal ${level}/5`}>
      <svg
        width="14"
        height="10"
        viewBox="0 0 14 10"
        className="flex-shrink-0"
        aria-label={`Signal ${level ?? 0}/5`}
      >
        {bars.map((h, i) => {
          const x = i * 3.2
          const y = 10 - h
          // bars 1-4 correspond to signal levels 1-4; level ≥4 lights all 4
          const isOn = active >= i + 1
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width="1.5"
              height={h}
              rx="0.3"
              className="fill-current"
              opacity={isOn ? 1 : 0.2}
              color={isOn ? 'hsl(150,65%,45%)' : 'currentColor'}
            />
          )
        })}
      </svg>
    </Tooltip>
  )
}

function SelectCheckbox({ selected }: { selected: boolean }) {
  return (
    <div
      className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
        selected ? 'bg-primary border-primary' : 'border-muted-foreground/40'
      }`}
    >
      {selected && (
        <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-primary-foreground">
          <path d="M1 4l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  )
}

// ── row ────────────────────────────────────────────────────────────────────────

interface CommandRowProps {
  camera: Camera
  selected: boolean
  selectionMode: boolean
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: Camera) => void
  onEditRequest: (camera: Camera) => void
  onMapRequest: (camera: Camera) => void
  onSimRequest: (camera: Camera) => void
  simUsageMB?: number | null
}

function CommandRow({
  camera,
  selected,
  selectionMode,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
  simUsageMB,
}: CommandRowProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('cameras')

  const handleClick = () => {
    if (selectionMode) { onToggleSelect(camera.id); return }
    if (camera.is_locked) {
      toast.error('Camera locked', { description: 'SIM mismatch — under review. Please contact support.' })
      return
    }
    navigate(`/events?camera=${camera.id}`)
  }

  return (
    <div
      data-testid="command-camera-row"
      className={`font-mono text-[12px] bg-card border border-border rounded-sm px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-accent/40 transition-colors duration-100 ${
        selected ? 'ring-1 ring-primary' : ''
      }`}
      onClick={handleClick}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div
          onClick={(e) => { e.stopPropagation(); onToggleSelect(camera.id) }}
        >
          <SelectCheckbox selected={selected} />
        </div>
      )}

      {/* LED dot */}
      <LedDot isOnline={camera.is_online} />

      {/* Camera name (sans) */}
      <span className="font-sans font-medium text-[13px] truncate flex-shrink-0 min-w-0 max-w-[160px]">
        {camera.name}
      </span>

      {/* IMEI mono muted */}
      <span className="text-muted-foreground tabular-nums truncate flex-shrink-0 hidden sm:block">
        {camera.imei}
      </span>

      {/* Battery bar */}
      <BatteryBar level={camera.battery_level} />

      {/* Signal bars (4-bar) */}
      <SignalBars4 level={camera.signal_level} />

      {/* Last seen — mono muted right-aligned spacer */}
      <span className="ml-auto text-muted-foreground tabular-nums whitespace-nowrap text-[11px]">
        {camera.last_seen
          ? formatRelativeTimeShort(camera.last_seen)
          : '—'}
      </span>

      {/* Actions dropdown */}
      {!selectionMode && (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditRequest(camera)}>
                <Settings className="mr-2 h-4 w-4" />
                {t('card.cameraSettings')}
              </DropdownMenuItem>
              {camera.latitude && camera.longitude && (
                <DropdownMenuItem onClick={() => onMapRequest(camera)}>
                  <MapPin className="mr-2 h-4 w-4" />
                  {t('card.viewOnMap')}
                </DropdownMenuItem>
              )}
              {camera.iccid && (
                <DropdownMenuItem onClick={() => onSimRequest(camera)}>
                  <Signal className="mr-2 h-4 w-4" />
                  {t('card.simDetails')}
                  {simUsageMB != null && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      {simUsageMB >= 1024
                        ? `${(simUsageMB / 1024).toFixed(1)} GB`
                        : `${Math.round(simUsageMB)} MB`}
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => onDeleteRequest(camera)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('card.deleteCamera')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────────

export interface CommandCameraBoardHandlers {
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: Camera) => void
  onEditRequest: (camera: Camera) => void
  onMapRequest: (camera: Camera) => void
  onSimRequest: (camera: Camera) => void
}

export interface CommandCameraBoardProps extends CommandCameraBoardHandlers {
  cameras: Camera[]
  selectionMode: boolean
  selectedIds: Set<number>
  simUsageMap: Record<string, { monthUsedMB: number }>
}

export function CommandCameraBoard({
  cameras,
  selectionMode,
  selectedIds,
  simUsageMap,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
}: CommandCameraBoardProps) {
  return (
    <div className="flex flex-col gap-1">
      {cameras.map((camera) => (
        <CommandRow
          key={camera.id}
          camera={camera}
          selected={selectedIds.has(camera.id)}
          selectionMode={selectionMode}
          onToggleSelect={onToggleSelect}
          onDeleteRequest={onDeleteRequest}
          onEditRequest={onEditRequest}
          onMapRequest={onMapRequest}
          onSimRequest={onSimRequest}
          simUsageMB={
            camera.iccid && simUsageMap[camera.iccid]
              ? simUsageMap[camera.iccid].monthUsedMB
              : null
          }
        />
      ))}
    </div>
  )
}
